import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {MultidocTableOpMap} from 'notebook/tables/excess/ops/TableOpConstants';

export interface CidIdMap {
  [cid: string]: string | number;
}

// A {containedNoteLinkedFileCid -> serverId} map embedded in clipboard HTML (under
// COPY_NOTE_LINKED_GROUP_CONTAINED_IDS) when a cross-entry-capable note-linked group (an image
// NotePartGrid or a stoichiometry/reaction group) is copied. Contained-file server ids live only
// in the source entry's cidIdMap, so they're smuggled through the clipboard to let a cross-entry
// paste re-register them in the destination entry.
export interface ContainedServerIdsFromClipboard {
  [containedFileCid: string]: string | number;
}

export interface CidNotePartProvenanceIdMap {
  [cid: string]: number;
}

export interface CidDocumentPartApiIdentifierMap {
  [cid: string]: string;
}

// Keep in sync with mediocre-noteEditable
export const NOTE_PADDING = 10;

// Provides a reasonable minimum amount of space in which to render the @-mention dropdown
export const NOTE_EDITOR_MIN_HEIGHT = '250px';

export const TIMESTAMP_WIDTH = 125; // keep in sync with mediocre.less:@mediocre-row-timestamp-width
export const BUFFER_OPTIONS_HEIGHT = 35; // keep in sync with less/variables.less:@height--buffer-options
export const COLLECTIBLE_BANNER_HEIGHT = 24; // keep in sync with less/variables.less:@height--collectible-banner
export const MISMATCH_WARNING_BANNER_HEIGHT = 28; // keep in sync with less/variables.less:@height--mismatch-warning-banner

export enum PRINT_OPTION {
  NOTE = 'note',
  ENTRY = 'entry',
  TEMPLATE = 'template',
  SELECTION = 'selection',
}

const NoteConstants = {
  TEXT: 'text',
  TABLE: 'table',
  CODE: 'code',
  OLIGO: 'oligo',

  BASIC_FOLDER_ITEM: ModelTypeConstants.BASIC_FOLDER_ITEM,
  ENTRY: ModelTypeConstants.ENTRY,
  NOTE_LINKED_APP_CANVAS: ModelTypeConstants.NOTE_LINKED_APP_CANVAS,
  NOTE_LINKED_ASSAY_DATA: ModelTypeConstants.NOTE_LINKED_ASSAY_DATA,
  NOTE_LINKED_OBJECT: ModelTypeConstants.NOTE_LINKED_OBJECT,
  NOTE_LINKED_FILE: ModelTypeConstants.NOTE_LINKED_FILE,
  PROTOCOL: ModelTypeConstants.PROTOCOL,
  SEQUENCE: ModelTypeConstants.SEQUENCE,
  TEMPLATE: ModelTypeConstants.TEMPLATE,
  NOTE_FILL_IN_TEXT_BOX: ModelTypeConstants.NOTE_FILL_IN_TEXT_BOX,
  ANNOTATION: ModelTypeConstants.ANNOTATION,
  PRIMER_PAIR: ModelTypeConstants.PRIMER_PAIR,
  PROTEIN: ModelTypeConstants.PROTEIN,
  SEQUENCE_ANALYSIS: ModelTypeConstants.SEQUENCE_ANALYSIS,
  SEQUENCE_PRIMER: ModelTypeConstants.SEQUENCE_PRIMER,

  NOTE_PREVIEW_SUPPORTED_TYPES: [
    ModelTypeConstants.BASIC_FOLDER_ITEM,
    ModelTypeConstants.ENTRY,
    ModelTypeConstants.PROTEIN,
    ModelTypeConstants.PROTOCOL,
    ModelTypeConstants.SEQUENCE,
  ],

  HEADER_1: 'header_1',
  HEADER_2: 'header_2',
  SUBHEADER_1: 'subheader_1',
  PARAGRAPH: 'paragraph', // used for toolbar, not exposed to backend

  STOICHIOMETRY_TABLE: 'stoichiometry_table',
  PLATE_CREATION_TABLE: 'plate_creation_table',
  BOX_CREATION_TABLE: 'box_creation_table',
  MIXTURE_PREP_GROUP: 'mixture_prep_group',
  FIXED_PLATE_EDITOR: 'fixed_plate_editor', // used for toolbar, not exposed to backend
  AI_CONTENT: 'ai_content', // used for toolbar, not exposed to backend
  AI_CHECKS: 'ai_checks', // used for toolbar, not exposed to backend
  AI_DATA_ENTRY: 'ai_data_entry', // used for toolbar, not exposed to backend

  COPY_NOTE_LINKED_OBJECT_DATA_ATTR_ID: 'data-mediocre-item-itemId',
  COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID: 'data-mediocre-parent-id',
  COPY_NOTE_LINKED_OBJECT_USER_ID: 'data-mediocre-user-id',
  // JSON map of {containedNoteLinkedFileCid: serverId} embedded in clipboard HTML when copying a
  // multi-image NotePartGrid (see ContainedServerIdsFromClipboard above). Lets a cross-entry paste
  // resolve contained-file server ids that otherwise live only in the source entry's cidIdMap.
  // Mirrors the single-id COPY_NOTE_LINKED_OBJECT_DATA_ATTR_ID pattern used for single-image previews.
  COPY_NOTE_LINKED_GROUP_CONTAINED_IDS: 'data-mediocre-group-contained-ids',
  MAX_NAME_LENGTH: 255,
};

export interface UpdateNoteOptions {
  performTableEvaluation?: boolean;
  updatedItemPartIndexes?: Array<number>;
  canIncrementalSave?: boolean;
  multidocTableOp?: MultidocTableOpMap;
  structuredTableMappings?: Array<StructuredTableMapping>;
}

export default NoteConstants;
